import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { Button, Dialog, DialogContent, DialogTitle, TextField, Typography } from '@material-ui/core';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { fetchLocations } from 'actions/Location/listAction';
import { isAdmin } from 'utils/Auth/hasRole';
import { CITY, COUNTRY, REGION, locationOptions, searchText } from 'utils/Location/locationOptions';
import LocationSearch from 'views/CompetitionSettings/components/Locations/components/LocationSearch';

const useStyles = makeStyles(theme => ({
    option: { display: 'flex', flexDirection: 'column' },
    context: { fontSize: 12, color: theme.palette.text.secondary },
    add: { marginTop: theme.spacing(0.5), textTransform: 'none' },
}));

const filter = createFilterOptions({ stringify: searchText });

/**
 * THE ONE WAY A PLACE IS CHOSEN (organiser, 18 Oct 2026: «щоб ввести
 * локацію, обрати необхідну, це просто ад … зробити в одному стилі, можливо
 * зробити єдиний компонент»).
 *
 * Type to search; every option shows the region and country above it, so
 * «Київ» the city and «Київ» the region are told apart; options are grouped
 * by country. `kinds` says what this form may hold — a club, an athlete and a
 * venue a CITY; a ФСТ a region or a country; a competition's scope regions or
 * countries ({@see locationOptions}). A value already stored of another kind
 * is still shown, with a note asking for the right one — never blanked.
 *
 * An administrator who does not find the place adds it from GeoNames without
 * leaving the form («Немає в списку? Знайти на GeoNames»); it is then chosen.
 *
 * The places are the store's (fetched once); nothing here is computed by the
 * server, so nothing is re-derived either.
 *
 * Stable Playwright hooks: `${testId}` on the input, `${testId}-option`
 * (data-location-id) on each option, `${testId}-add` on the GeoNames button.
 */
const LocationPicker = ({
    kinds, value, onChange, multiple, label, required, error, helperText, disabled, restrictTo, testId, allowAdd, className,
}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [t] = useTranslation();
    const locations = useSelector(store => store.locations || {});
    const user = useSelector(store => (store.userSession && store.userSession.user) || {});
    const [adding, setAdding] = useState(false);

    useEffect(() => {
        if (!locations.isFetched && !locations.isFetching) {
            dispatch(fetchLocations());
        }
    }, [dispatch, locations.isFetched, locations.isFetching]);

    const ids = multiple ? (value || []) : [value].filter(Boolean);
    const idsKey = ids.join('|');
    const options = useMemo(
        () => locationOptions(locations.data, kinds, { restrictTo, keep: ids }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [locations.data, kinds.join('|'), restrictTo, idsKey],
    );
    const byId = useMemo(() => {
        const map = {};
        options.forEach(o => { map[o.id] = o; });

        return map;
    }, [options]);
    const selected = multiple ? ids.map(id => byId[id]).filter(Boolean) : (byId[value] || null);
    const wrongKind = ids.map(id => byId[id]).find(o => o && o.outOfKind);
    const kindLabel = t(`component.locationPicker.kinds.${kinds.join('-')}`, kinds.join(' / '));

    const imported = location => {
        setAdding(false);
        dispatch(fetchLocations());
        if (location && location.id) {
            onChange(multiple ? [...ids, location.id] : location.id);
        }
    };

    return (
        <div className={className}>
            <Autocomplete
                multiple={multiple}
                options={options}
                value={selected}
                disabled={disabled}
                disableClearable={required && !multiple}
                filterOptions={filter}
                groupBy={o => o.group}
                getOptionLabel={o => (o && o.title) || ''}
                getOptionSelected={(o, v) => !!v && o.id === v.id}
                getOptionDisabled={o => o.outOfKind}
                onChange={(event, picked) => onChange(multiple ? picked.map(o => o.id) : (picked ? picked.id : null))}
                noOptionsText={t('component.locationPicker.nothing', 'Nothing found')}
                renderOption={o => (
                    <div className={classes.option} data-testid={`${testId}-option`} data-location-id={o.id}>
                        <span>{o.title}</span>
                        {o.context && <span className={classes.context}>{o.context}</span>}
                    </div>
                )}
                renderInput={params => (
                    <TextField
                        {...params}
                        label={label}
                        required={required}
                        variant="outlined"
                        error={error}
                        helperText={helperText || (wrongKind
                            ? t('component.locationPicker.wrongKind', { defaultValue: '«{{place}}» is not a {{kind}} — choose one.', place: wrongKind.title, kind: kindLabel })
                            : null)}
                        inputProps={{ ...params.inputProps, 'data-testid': testId }}
                    />
                )}
            />
            {allowAdd && isAdmin(user) && !disabled && (
                <Button size="small" className={classes.add} onClick={() => setAdding(true)} data-testid={`${testId}-add`}>
                    {t('component.locationPicker.add', 'Not in the list? Find it on GeoNames')}
                </Button>
            )}
            <Dialog open={adding} onClose={() => setAdding(false)} fullWidth maxWidth="md">
                <DialogTitle>{t('component.locationPicker.addTitle', 'Find a place on GeoNames')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" gutterBottom>
                        {t('component.locationPicker.addHint', { defaultValue: 'Add it with «Add», or pick one already here with «Show» — it is then chosen in the form. Allowed here: {{kind}}.', kind: kindLabel })}
                    </Typography>
                    <LocationSearch onReveal={imported} onImported={imported} />
                </DialogContent>
            </Dialog>
        </div>
    );
};

LocationPicker.propTypes = {
    kinds: PropTypes.arrayOf(PropTypes.oneOf([COUNTRY, REGION, CITY])).isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    onChange: PropTypes.func.isRequired,
    multiple: PropTypes.bool,
    label: PropTypes.string,
    required: PropTypes.bool,
    error: PropTypes.bool,
    helperText: PropTypes.node,
    disabled: PropTypes.bool,
    restrictTo: PropTypes.arrayOf(PropTypes.string),
    testId: PropTypes.string,
    allowAdd: PropTypes.bool,
    className: PropTypes.string,
};

LocationPicker.defaultProps = {
    value: null,
    multiple: false,
    label: '',
    required: false,
    error: false,
    helperText: null,
    disabled: false,
    restrictTo: null,
    testId: 'location-picker',
    allowAdd: false,
    className: undefined,
};

export default LocationPicker;
